import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthContext } from "../../context/Context";
import { setUserToStorage } from "../../services/auth.util";
import { API_ERRORS, authAPI, getErrorType } from "../../services/api.util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const inputBaseStyle = {
  width: "100%",
  padding: "14px 16px 14px 48px",
  borderRadius: "12px",
  fontSize: "15px",
  transition: "all 0.2s ease",
  backgroundColor: "#fff",
  outline: "none"
};

const getFieldError = (error, fallback) => {
  const errorType = getErrorType(error);

  if (errorType === API_ERRORS.VALIDATION_ERROR && Array.isArray(error.data?.errors)) {
    const fieldErrors = {};
    error.data.errors.forEach((err) => {
      if (err.path) {
        fieldErrors[err.path] = err.msg;
      }
    });
    return { fieldErrors };
  }

  if (errorType === API_ERRORS.NETWORK_ERROR) {
    return { general: "Unable to connect to server. Please check your internet connection." };
  }

  if (errorType === API_ERRORS.SERVER_ERROR) {
    return { general: error.data?.message || "Server error. Please try again later." };
  }

  return { general: error.data?.message || error.message || fallback };
};

export default function LoginForm() {
  const navigate = useNavigate();
  const { setLoggedIn, setLoggedInUser } = useAuthContext();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [method, setMethod] = useState("phone");
  const [formData, setFormData] = useState({
    identifier: "",
    otp: ""
  });
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((value) => Math.max(value - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const identifierLabel = method === "email" ? "Email Address" : "WhatsApp Phone Number";
  const identifierPlaceholder = method === "email" ? "Enter your email" : "Enter your WhatsApp phone number";

  const resetFlow = (nextMethod) => {
    setMethod(nextMethod);
    setFormData({ identifier: "", otp: "" });
    setOtpSent(false);
    setErrors({});
    setResendCooldown(0);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "otp" || (name === "identifier" && method === "phone")
      ? value.replace(/\D/g, "").slice(0, name === "otp" ? 6 : 10)
      : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    if (errors[name] || errors.general) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
        general: ""
      }));
    }
  };

  const validateIdentifier = () => {
    const newErrors = {};
    const identifier = formData.identifier.trim();

    if (!identifier) {
      newErrors.identifier = `${identifierLabel} is required`;
    } else if (method === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
      newErrors.identifier = "Please enter a valid email address";
    } else if (method === "phone") {
      const digits = identifier.replace(/\D/g, "");
      if (!/^\d{10}$/.test(identifier) || digits.length !== 10) {
        newErrors.identifier = "Please enter a valid 10-digit WhatsApp phone number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOTP = () => {
    const newErrors = {};

    if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = "OTP must be exactly 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const applyAuthenticatedUser = (user, token) => {
    setLoggedIn(true);
    setLoggedInUser({
      uid: user._id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      role: user.role,
      isEmailVerified: user.isEmailVerified,
      isOTPVerified: user.isOTPVerified,
      profileImage: user.profileImage,
      mobileNumber: user.mobileNumber,
      address: user.address,
      state: user.state,
      country: user.country,
      personalInfo: user.personalInfo
    });

    setUserToStorage({
      ...user,
      token
    });
  };

  const sendOTP = async () => {
    if (!validateIdentifier()) return;

    setLoading(true);
    setErrors({});

    try {
      await authAPI.sendLoginOTP({
        method,
        identifier: formData.identifier.trim()
      });

      setOtpSent(true);
      setFormData((prev) => ({ ...prev, otp: "" }));
      setResendCooldown(60);
    } catch (error) {
      console.error("Send login OTP error:", error);
      const parsedError = getFieldError(error, "Failed to send OTP. Please try again.");
      setErrors(parsedError.fieldErrors || { general: parsedError.general });
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!validateOTP()) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await authAPI.verifyLoginOTP({
        method,
        identifier: formData.identifier.trim(),
        otp: formData.otp
      });

      const { user, token } = response.data;
      applyAuthenticatedUser(user, token);
      navigate("/");
    } catch (error) {
      console.error("Verify login OTP error:", error);
      const parsedError = getFieldError(error, "Login failed. Please try again.");
      setErrors(parsedError.fieldErrors || { general: parsedError.general });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (otpSent) {
      verifyOTP();
    } else {
      sendOTP();
    }
  };

  const iconStroke = method === "email" ? "#aeb4bf" : "#111827";
  const inputIcon = method === "email" ? (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h16v12H4V6Z" stroke={iconStroke} strokeWidth="2" strokeLinejoin="round" />
      <path d="m4 7 8 6 8-6" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 4h10v16H7V4Z" stroke={iconStroke} strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 17h4" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const sendIcon = (
    <svg width="31" height="31" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 3 9.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m21 3-7 18-4.5-6.5L3 10l18-7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div className="form-page__content" style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: isMobile ? "20px 15px" : "20px",
      width: "100%"
    }}>
      <div className="container" style={{ maxWidth: "520px", width: "100%", padding: 0 }}>
        <div className="login-card" style={{
          background: "#fff",
          borderRadius: isMobile ? "28px" : "34px",
          padding: isMobile ? "34px 22px" : "36px 38px 32px",
          boxShadow: "0 28px 70px rgba(17, 24, 39, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.95)",
          border: "1px solid rgba(255, 255, 255, 0.92)",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(20px)"
        }}>
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "#fff",
            pointerEvents: "none",
            zIndex: 0
          }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ textAlign: "center", marginBottom: "30px" }}>
              <h2 style={{
                fontSize: "28px",
                fontWeight: "bold",
                color: "#000000",
                marginBottom: "8px"
              }}>
                Welcome Back
              </h2>
              <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: "2px" }}>
                Sign in with a one-time password.
              </p>
              <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
                New users can join with WhatsApp OTP. Email OTP is for existing accounts.
              </p>
            </div>

            {errors.general && (
              <div style={{
                padding: "12px 8px",
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "8px",
                color: "#721c24",
                marginBottom: "12px",
                fontSize: "14px",
                lineHeight: 1.5
              }}>
                {errors.general}
              </div>
            )}

            <div style={{ marginBottom: "32px" }}>
              <div style={{
                textAlign: "center",
                color: "#4b5563",
                fontSize: "12px",
                fontWeight: "700",
                letterSpacing: "1px",
                marginBottom: "12px"
              }}>
                CHOOSE LOGIN METHOD
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                borderBottom: "1px solid #d9dde5"
              }}>
                {[
                  {
                    value: "phone",
                    label: "WhatsApp OTP",
                    icon: (
                      <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: isMobile ? "20px" : "26px" }} />
                    )
                  },
                  {
                    value: "email",
                    label: "Email OTP",
                    icon: (
                      <svg width={isMobile ? "22" : "28"} height={isMobile ? "22" : "28"} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )
                  }
                ].map((option) => {
                  const isActive = method === option.value;

                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => resetFlow(option.value)}
                      disabled={loading}
                      style={{
                        padding: isMobile ? "10px 4px 14px" : "14px 10px 18px",
                        border: "none",
                        borderBottom: isActive ? "3px solid #000" : "3px solid transparent",
                        background: "transparent",
                        color: isActive ? "#111827" : "#4b5563",
                        fontWeight: isActive ? "800" : "600",
                        fontSize: isMobile ? "14px" : "16px",
                        cursor: loading ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: isMobile ? "7px" : "14px",
                        whiteSpace: "nowrap",
                        transform: "translateY(1px)"
                      }}
                    >
                      {option.icon}
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div>
                <label style={{
                  display: "block",
                  fontSize: "15px",
                  fontWeight: "800",
                  color: "#2d3748",
                  marginBottom: "12px"
                }}>
                  {identifierLabel}
                </label>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "inline-flex",
                    color: "#aeb4bf"
                  }}>
                    {inputIcon}
                  </span>
                  <input
                    required
                    type={method === "email" ? "email" : "tel"}
                    name="identifier"
                    placeholder={identifierPlaceholder}
                    value={formData.identifier}
                    onChange={handleInputChange}
                    disabled={loading || otpSent}
                    inputMode={method === "phone" ? "numeric" : undefined}
                    maxLength={method === "phone" ? 10 : undefined}
                    style={{
                      ...inputBaseStyle,
                      border: errors.identifier ? "2px solid #dc3545" : "1px solid #d9dde5",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)"
                    }}
                  />
                </div>
                {errors.identifier && (
                  <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                    {errors.identifier}
                  </div>
                )}
              </div>

              {otpSent && (
                <>
                  <div style={{
                    padding: "12px 16px",
                    borderRadius: "8px",
                    backgroundColor: "#f4f7f4",
                    border: "1px solid #dce7dc",
                    color: "#31533a",
                    fontSize: "14px"
                  }}>
                    OTP sent to {method === "email" ? "your email" : "your WhatsApp number"}. It expires in 10 minutes.
                  </div>

                  <div>
                    <label style={{
                      display: "block",
                      fontSize: "15px",
                      fontWeight: "800",
                      color: "#2d3748",
                      marginBottom: "12px"
                    }}>
                      Enter 6-Digit OTP
                    </label>
                    <input
                      required
                      type="text"
                      name="otp"
                      placeholder="000000"
                      value={formData.otp}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={6}
                      style={{
                        ...inputBaseStyle,
                        paddingLeft: "18px",
                        border: errors.otp ? "2px solid #dc3545" : "1px solid #d9dde5",
                        fontSize: "24px",
                        textAlign: "center",
                        letterSpacing: "8px",
                        fontFamily: "monospace",
                        fontWeight: "600"
                      }}
                    />
                    {errors.otp && (
                      <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                        {errors.otp}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={sendOTP}
                    disabled={loading || resendCooldown > 0}
                    style={{
                      background: "none",
                      border: "none",
                      color: resendCooldown > 0 ? "#9ca3af" : "#667eea",
                      cursor: loading || resendCooldown > 0 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      textDecoration: "underline",
                      padding: "0"
                    }}
                  >
                    {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
                  </button>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: isMobile ? "14px 20px" : "14px 20px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "17px",
                  fontWeight: "800",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  transition: "all 0.2s ease",
                  marginTop: "6px",
                  boxShadow: "0 12px 22px rgba(0, 0, 0, 0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "14px"
                }}
              >
                {!loading && !otpSent && sendIcon}
                {loading ? (otpSent ? "Verifying..." : "Sending OTP...") : (otpSent ? "Verify & Login" : "Send OTP")}
              </button>

              {otpSent && (
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setFormData((prev) => ({ ...prev, otp: "" }));
                    setErrors({});
                    setResendCooldown(0);
                  }}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6c757d",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: "14px"
                  }}
                >
                  Change {method === "email" ? "email" : "phone number"}
                </button>
              )}
            </form>

            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              color: "#7b808a",
              fontSize: "15px",
              marginTop: "28px"
            }}>
              <span style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 8px 18px rgba(17, 24, 39, 0.10)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 3 5 6v5c0 4.5 2.9 8.5 7 10 4.1-1.5 7-5.5 7-10V6l-7-3Z" stroke="#7b808a" strokeWidth="2" strokeLinejoin="round" />
                  <path d="m9 12 2 2 4-4" stroke="#7b808a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>Secure &amp; encrypted authentication</span>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .login-card input:focus {
            border-color: #c7ccd5 !important;
            box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.04) !important;
          }

          @media (max-width: 768px) {
            .login-card {
              padding: 30px 20px !important;
            }
          }
        `}
      </style>
    </div>
  );
}
